import type { ReactNode } from 'react';
import { useMemo, useRef, useState } from 'react';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Alert, Box, Button, Chip, CircularProgress, Divider, Link, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { EmployerFootprintAdminViewDto } from '../../api/employerVerificationDashboardApi';
import {
  buildEmployerFootprintPlainStory,
  buildFootprintDriverRows,
  buildFootprintOsintTree,
  footprintConfidenceDetail,
  footprintOperatorReason,
  footprintScoreBand,
  formatFootprintPurchaseArea,
  type FootprintDriverRow,
  type FootprintScoreBand,
} from '../../lib/employerFootprintCopy';
import { formatMismatchWarning } from '../../lib/employerResolvedLocation';
import { ApplicationSectionHeader } from './applicationSectionChrome';
import { EmployerFootprintOsintGraph } from './EmployerFootprintOsintGraph';
import { EmployerFootprintViewFrame } from './EmployerFootprintViewFrame';
import { EmployerSiteInspector } from './EmployerSiteInspector';
import { useFootprintMapPins } from './useFootprintMapPins';

function FootprintKvRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(128px, 148px) 1fr' },
        columnGap: 1.5,
        rowGap: 0.25,
        py: 0.9,
        alignItems: 'baseline',
      }}
    >
      <Typography
        component="dt"
        sx={{
          fontSize: '0.75rem',
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: 'text.primary',
          opacity: 0.62,
          lineHeight: 1.35,
        }}
      >
        {label}
      </Typography>
      <Box
        component="dd"
        sx={{
          m: 0,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: 'text.primary',
          lineHeight: 1.45,
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Box>
    </Box>
  );
}

function ExternalUrl({ href }: { href: string }) {
  const display = href.replace(/^https?:\/\//i, '').replace(/\/$/, '');
  return (
    <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontWeight: 500 }}>
      {display}
    </Link>
  );
}

function normalizeHttpUrl(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;
  if (/^https?:\/\//i.test(t)) return t;
  if (/^[\w.-]+\.[a-z]{2,}/i.test(t)) return `https://${t}`;
  return null;
}

function SourceHint({ text }: { text: string }) {
  return (
    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75, fontWeight: 500 }}>
      ({text})
    </Typography>
  );
}

const FLAG_FACE: Record<FootprintScoreBand, { ink: string; fill: string; fillHi: string }> = {
  high: { ink: '#1f6a6a', fill: '#c5e4de', fillHi: '#e8f6f3' },
  mid: { ink: '#6b5c2e', fill: '#e8dfc4', fillHi: '#f6f1e2' },
  low: { ink: '#4a3f78', fill: '#d9d2ea', fillHi: '#f2eff8' },
};

const FLAG_ORDER = ['payor', 'web', 'geo'] as const;

const SIGNAL_STRENGTH_LABEL: Record<FootprintScoreBand, string> = {
  high: 'Strong signal',
  mid: 'Moderate signal',
  low: 'Weak signal',
};

function FootprintSignalFlags({ rows }: { rows: FootprintDriverRow[] }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const shown = FLAG_ORDER.map((id) => rows.find((row) => row.id === id)).filter(
    (row): row is FootprintDriverRow => row != null && footprintScoreBand(row.score) != null,
  );
  if (shown.length === 0) return null;
  const open = shown.find((row) => row.id === openId) ?? null;

  return (
    <Box sx={{ mt: 0.85 }} data-testid="employer-footprint-signal-flags">
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75}>
        {shown.map((row) => {
          const band = footprintScoreBand(row.score)!;
          const face = FLAG_FACE[band];
          const strengthLabel = SIGNAL_STRENGTH_LABEL[band];
          const selected = openId === row.id;
          return (
            <Box
              key={row.id}
              component="button"
              type="button"
              onClick={() => setOpenId(selected ? null : row.id)}
              aria-expanded={selected}
              aria-label={`${row.label}, ${strengthLabel}, model estimate`}
              data-testid={`employer-footprint-flag-${row.id}`}
              sx={{
                appearance: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                minWidth: 92,
                minHeight: 44,
                px: 1,
                py: 0.55,
                borderRadius: '8px',
                border: '1px solid',
                borderColor: selected ? face.ink : `${face.ink}40`,
                background: `linear-gradient(155deg, ${face.fillHi} 0%, ${face.fill} 70%)`,
                boxShadow: selected
                  ? `inset 0 1px 0 rgba(255,255,255,0.75), 0 4px 10px ${face.ink}22`
                  : 'inset 0 1px 0 rgba(255,255,255,0.7)',
              }}
            >
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: face.ink, lineHeight: 1.2 }}>
                {row.label}
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: face.ink, lineHeight: 1.25 }}>
                {strengthLabel}
              </Typography>
            </Box>
          );
        })}
      </Stack>
      {open ? (
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5} sx={{ mt: 0.7 }}>
          {open.chips.map((chip) => (
            <Chip
              key={chip.id}
              size="small"
              variant="outlined"
              label={chip.label}
              sx={{ height: 22, fontWeight: 500, fontSize: '0.7rem' }}
            />
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}

export function ApplicationEmployerFootprintPanel({
  view,
  declaredEmployerAddress: declaredEmployerAddressProp,
  subdued = false,
}: {
  view: EmployerFootprintAdminViewDto;
  declaredEmployerAddress?: string | null;
  /** When formal findings exist, keep footprint as supporting evidence. */
  subdued?: boolean;
}) {
  const status = String(view.status ?? '').toUpperCase();
  const evidence = view.locationEvidence;
  const serverPurchases = evidence?.purchasesWithCoordinates ?? [];
  const serverEmployerPin = evidence?.possibleEmployer ?? null;
  const aiFootprintAddress = (view.address ?? '').trim() || null;
  const declaredEmployerAddress =
    (declaredEmployerAddressProp ?? view.declaredEmployerAddress ?? '').trim() || null;
  const mapPins = useFootprintMapPins({
    purchases: serverPurchases,
    employer: serverEmployerPin,
    employerAddressFallback: declaredEmployerAddress,
    employerNameFallback: view.detectedEmployer,
    purchaseAreaFallback: evidence?.geographicCluster,
  });
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [graphOpen, setGraphOpen] = useState(false);
  const graphOpenerRef = useRef<HTMLButtonElement>(null);

  const story = useMemo(() => buildEmployerFootprintPlainStory(view), [view]);
  const profile = view.companyProfile ?? null;
  const purchases = mapPins.purchases;
  const employerPin = mapPins.employer;
  const resolved = mapPins.resolvedLocation;
  const hasMap = mapPins.showMap;
  const distanceToPurchasesKm = resolved?.distanceKm ?? null;
  const distanceDerivation = resolved?.distanceDerivation ?? null;
  const purchaseArea = formatFootprintPurchaseArea(evidence?.geographicCluster, {
    purchasesAnalyzed: evidence?.purchasesAnalyzed,
  });

  const severity =
    story.tone === 'success'
      ? 'success'
      : story.tone === 'warning'
        ? 'warning'
        : story.tone === 'error'
          ? 'error'
          : 'info';

  const confidencePct =
    view.overallConfidence != null && Number.isFinite(view.overallConfidence)
      ? Math.round(view.overallConfidence * 100)
      : null;

  const websiteHref = profile?.officialWebsite ? normalizeHttpUrl(profile.officialWebsite) : null;
  const linkedInHref = profile?.linkedInUrl ? normalizeHttpUrl(profile.linkedInUrl) : null;
  const osintTree = useMemo(
    () =>
      buildFootprintOsintTree({
        companyName: view.detectedEmployer,
        profile,
        declaredAddress: declaredEmployerAddress,
        aiAddress: aiFootprintAddress,
        purchaseArea,
        phone: view.phone,
        mismatchMeters: resolved?.mismatch?.distanceMeters ?? null,
        distanceKm: distanceToPurchasesKm,
      }),
    [
      view.detectedEmployer,
      view.phone,
      profile,
      declaredEmployerAddress,
      aiFootprintAddress,
      purchaseArea,
      resolved?.mismatch?.distanceMeters,
      distanceToPurchasesKm,
    ],
  );

  const employerLat = resolved?.lat ?? null;
  const employerLng = resolved?.lng ?? null;
  const coordSourceHint =
    resolved?.source === 'geocode'
      ? 'from declared address'
      : resolved?.source === 'server'
        ? 'from AI'
        : resolved?.source === 'search'
          ? 'from search'
          : null;

  const detailRows: { label: string; value: ReactNode }[] = [];
  const operatorReason = footprintOperatorReason(view);
  if (operatorReason) {
    detailRows.push({ label: 'Operator reason', value: operatorReason });
  }
  if (view.paystubsRequiredReason && view.paystubsRequiredReason !== operatorReason) {
    detailRows.push({ label: 'Reason code', value: view.paystubsRequiredReason });
  }
  if (view.detectedEmployer) {
    detailRows.push({
      label: 'Suggested employer',
      value: (
        <Link
          ref={graphOpenerRef}
          component="button"
          type="button"
          onClick={() => setGraphOpen(true)}
          aria-label={`Open employer graph for ${view.detectedEmployer}`}
          title="Open employer graph"
          sx={{ fontWeight: 600, verticalAlign: 'baseline' }}
          data-testid="employer-footprint-open-graph"
        >
          {view.detectedEmployer}
        </Link>
      ),
    });
  }
  if (declaredEmployerAddress) {
    detailRows.push({ label: 'Employer declared address', value: declaredEmployerAddress });
  }
  if (aiFootprintAddress) {
    detailRows.push({ label: 'AI footprint address', value: aiFootprintAddress });
  }
  if (employerLat != null) {
    detailRows.push({
      label: 'Latitude',
      value: (
        <>
          {employerLat.toFixed(6)}
          {coordSourceHint ? <SourceHint text={coordSourceHint} /> : null}
        </>
      ),
    });
  }
  if (employerLng != null) {
    detailRows.push({
      label: 'Longitude',
      value: (
        <>
          {employerLng.toFixed(6)}
          {coordSourceHint ? <SourceHint text={coordSourceHint} /> : null}
        </>
      ),
    });
  }
  if (view.phone) {
    detailRows.push({ label: 'Phone', value: view.phone });
  }
  if (websiteHref) {
    detailRows.push({ label: 'Website', value: <ExternalUrl href={websiteHref} /> });
  } else if (profile?.officialWebsite) {
    detailRows.push({ label: 'Website', value: profile.officialWebsite });
  }
  if (linkedInHref) {
    detailRows.push({ label: 'LinkedIn', value: <ExternalUrl href={linkedInHref} /> });
  } else if (profile?.linkedInUrl?.trim()) {
    detailRows.push({ label: 'LinkedIn', value: profile.linkedInUrl.trim() });
  }
  if (profile?.businessNumber) {
    detailRows.push({ label: 'Business number', value: profile.businessNumber });
  }
  if (profile?.corporationId) {
    detailRows.push({ label: 'Corporation ID', value: profile.corporationId });
  }
  if (profile?.sector) {
    detailRows.push({ label: 'Sector', value: profile.sector });
  }
  if (profile?.companyOriginCountry) {
    detailRows.push({ label: 'Origin country', value: profile.companyOriginCountry });
  }
  if (distanceToPurchasesKm != null) {
    detailRows.push({
      label: 'Distance to purchases',
      value: (
        <>
          {`About ${distanceToPurchasesKm.toFixed(1)} km`}
          {distanceDerivation === 'recomputed' ? (
            <SourceHint text="recomputed" />
          ) : distanceDerivation === 'server' ? (
            <SourceHint text="from AI" />
          ) : null}
        </>
      ),
    });
  }
  if (evidence?.geographicCluster) {
    detailRows.push({
      label: 'Purchase area',
      value: purchaseArea ?? evidence.geographicCluster,
    });
  }
  const confidenceInStory = story.detailPoints.some((point) => /confidence/i.test(point));
  if (confidencePct != null && !confidenceInStory) {
    const qualified = footprintConfidenceDetail(`${confidencePct}%`, {
      confirmPath: view.journeyHint === 'CONFIRM',
      hasOperatorReason: Boolean(operatorReason),
    });
    if (qualified) {
      detailRows.push({ label: 'Model estimate', value: qualified });
    }
  }
  const signalRows = buildFootprintDriverRows({
    profile,
    purchaseArea,
    distanceKm: distanceToPurchasesKm,
    declaredGeocoded: mapPins.usedGeocodeFallback,
    mismatchMeters: resolved?.mismatch?.distanceMeters ?? null,
  });
  const signalFlags = <FootprintSignalFlags rows={signalRows} />;

  if (evidence && evidence.purchasesAnalyzed > 0) {
    detailRows.push({
      label: 'Purchases mapped',
      value: (
        <>
          {`${mapPins.serverCoordPurchaseCount} of ${evidence.purchasesAnalyzed} with coordinates${
            mapPins.usedGeocodeFallback && mapPins.purchases.length > mapPins.serverCoordPurchaseCount
              ? ` · ${mapPins.purchases.length - mapPins.serverCoordPurchaseCount} from address`
              : mapPins.usedGeocodeFallback && mapPins.serverCoordPurchaseCount === 0
                ? ' · plotted from addresses'
                : ''
          }`}
          {signalFlags}
        </>
      ),
    });
  } else if (signalRows.some((row) => footprintScoreBand(row.score) != null)) {
    detailRows.push({ label: 'Signals', value: signalFlags });
  }

  if (status === 'NOT_STARTED') return null;

  const detailsBlock =
    detailRows.length > 0 ? (
      <Box
        component="dl"
        sx={{
          m: 0,
          height: '100%',
          px: 1.35,
          py: 0.35,
          borderRadius: 2,
          border: '1px solid',
          borderColor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.12 : 0.22),
          bgcolor: 'background.paper',
          boxShadow: (t) =>
            t.palette.mode === 'light'
              ? 'inset 0 1px 0 rgba(255,255,255,0.85)'
              : 'inset 0 1px 0 rgba(255,255,255,0.06)',
          '@media (prefers-contrast: more)': {
            borderColor: 'divider',
            boxShadow: 'none',
          },
        }}
      >
        {detailRows.map((row, i) => (
          <Box key={row.label}>
            {i > 0 ? <Divider /> : null}
            <FootprintKvRow label={row.label} value={row.value} />
          </Box>
        ))}
      </Box>
    ) : null;

  const mapBlock = hasMap ? (
    <Box
      sx={{
        minWidth: 0,
        height: '100%',
        minHeight: { md: subdued ? 360 : 440 },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <PlaceRoundedIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
          Purchases and possible employer
        </Typography>
        {mapPins.usedGeocodeFallback ? (
          <Chip size="small" label="Geocoded from declared address" variant="outlined" sx={{ height: 22, fontWeight: 500 }} />
        ) : null}
        {employerPin ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<VideocamRoundedIcon fontSize="small" />}
            onClick={() => setInspectorOpen(true)}
            data-testid="employer-site-inspect-button"
            sx={{ ml: 'auto', textTransform: 'none', fontWeight: 500, py: 0.25 }}
          >
            Inspect site
          </Button>
        ) : null}
      </Stack>
      {resolved?.mismatch ? (
        <Alert
          severity="warning"
          variant="outlined"
          icon={<WarningAmberRoundedIcon fontSize="inherit" />}
          sx={{ mb: 1, py: 0.35 }}
          data-testid="employer-coord-mismatch-alert"
        >
          <Typography variant="caption" sx={{ fontWeight: 500, lineHeight: 1.4 }}>
            {formatMismatchWarning(resolved.mismatch)}
          </Typography>
        </Alert>
      ) : null}
      {mapPins.geocodeStatus === 'error' && mapPins.geocodeError && !employerPin && purchases.length === 0 ? (
        <Alert severity="error" variant="outlined" sx={{ mb: 1, py: 0.35 }}>
          <Typography variant="caption" sx={{ fontWeight: 500 }}>
            {mapPins.geocodeError}
          </Typography>
        </Alert>
      ) : mapPins.geocodeError ? (
        <Alert severity="warning" variant="outlined" sx={{ mb: 1, py: 0.35 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
            {mapPins.geocodeError}
            {purchases.length > 0 || employerPin
              ? ' Some purchase or employer locations could not be plotted.'
              : ''}
          </Typography>
        </Alert>
      ) : null}
      <Box
        sx={{
          flex: 1,
          minHeight: { xs: subdued ? 260 : 300, md: subdued ? 300 : 360 },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {mapPins.geocoding ? (
          <Box
            sx={{
              flex: 1,
              minHeight: subdued ? 260 : 300,
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Stack spacing={1} alignItems="center">
              <CircularProgress size={28} />
              <Typography variant="caption" color="text.secondary">
                Locating address on map…
              </Typography>
            </Stack>
          </Box>
        ) : (
          <EmployerFootprintViewFrame
            purchases={purchases}
            employer={employerPin}
            fillHeight
          />
        )}
      </Box>
      {employerPin ? (
        <EmployerSiteInspector
          open={inspectorOpen}
          onClose={() => setInspectorOpen(false)}
          employer={{
            name: employerPin.name,
            address: employerPin.address,
            latitude: employerPin.latitude,
            longitude: employerPin.longitude,
          }}
        />
      ) : null}
    </Box>
  ) : null;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: subdued ? 1.35 : 1.75,
        borderRadius: 2,
        bgcolor: 'background.paper',
        borderColor: (t) => alpha(t.palette.divider, t.palette.mode === 'light' ? 1 : 0.9),
        boxShadow: (t) =>
          t.palette.mode === 'light'
            ? '0 1px 2px rgba(15, 23, 42, 0.05), 0 10px 28px rgba(15, 23, 42, 0.06)'
            : '0 10px 28px rgba(0, 0, 0, 0.35)',
        '@media (prefers-reduced-transparency: reduce)': {
          bgcolor: 'background.paper',
          boxShadow: (t) =>
            t.palette.mode === 'light' ? '0 1px 3px rgba(15, 23, 42, 0.08)' : '0 4px 16px rgba(0,0,0,0.4)',
        },
      }}
      data-testid="employer-footprint-panel"
      data-tour-target="employer-footprint-panel"
    >
      <ApplicationSectionHeader
        title="Digital footprint"
        subtitle={
          subdued
            ? 'Bank spending and purchase map for context only. Flagged items are in the verification brief above.'
            : 'Bank spending used to suggest the employer — before formal verification'
        }
        icon={<TravelExploreRoundedIcon fontSize="small" />}
        endAdornment={
          <Chip
            size="small"
            label={
              status === 'RUNNING' || status === 'PENDING'
                ? 'In progress'
                : status === 'READY'
                  ? 'Ready'
                  : status === 'FAILED'
                    ? 'Failed'
                    : status
            }
            color={
              status === 'READY'
                ? 'success'
                : status === 'FAILED'
                  ? 'error'
                  : status === 'RUNNING' || status === 'PENDING'
                    ? 'warning'
                    : 'default'
            }
            variant="outlined"
            sx={{ height: 22, fontWeight: 500 }}
          />
        }
        mb={1.5}
      />

      <Alert severity={severity} variant="outlined" sx={{ mb: detailRows.length || hasMap ? 1.5 : 0, py: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {story.headline}
        </Typography>
        {story.detailPoints.length > 0 ? (
          <Box
            component="ul"
            sx={{
              m: 0,
              mt: 0.5,
              pl: 2.25,
              color: 'text.secondary',
              '& li': { lineHeight: 1.45, mb: 0.35 },
              '& li:last-of-type': { mb: 0 },
            }}
          >
            {story.detailPoints.map((point) => (
              <Typography key={point} component="li" variant="body2">
                {point}
              </Typography>
            ))}
          </Box>
        ) : story.detail ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.45 }}>
            {story.detail}
          </Typography>
        ) : null}
      </Alert>

      {detailsBlock && mapBlock ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 0.95fr) minmax(0, 1.35fr)' },
            gap: 2,
            alignItems: 'stretch',
            minHeight: { md: subdued ? 380 : 460 },
          }}
        >
          {detailsBlock}
          {mapBlock}
        </Box>
      ) : (
        <>
          {detailsBlock}
          {mapBlock}
        </>
      )}

      <EmployerFootprintOsintGraph
        open={graphOpen}
        onClose={() => setGraphOpen(false)}
        tree={osintTree}
        returnFocusRef={graphOpenerRef}
      />
    </Paper>
  );
}
